"use client";

import Link from "next/link";
import { useState } from "react";
import CountryCodeSelector from "~/components/themed/CountryCodeSelector";
import PhoneNumberInput from "~/components/themed/PhoneNumberInput";
import { api } from "~/utils/api";
import { Button } from "../v3/themed/Button";
import Modal from "../v3/themed/Modal";
import VerifyPhoneNumberModal from "./VerifyPhoneNumberModal";
import { useSession } from "next-auth/react";

type PhoneNumberModalProps = {
    mode: "add" | "edit";
    showModal: boolean;
    setShowModal: (show: boolean) => void;
    isUserPhoneNumberVerified?: boolean | null;
};

const PhoneNumberModal = (props: PhoneNumberModalProps) => {
    const { data: session, update: updateSession } = useSession();
    const { data: user } = api.user.getUserById.useQuery(
        { userId: session?.user?.id ?? "" },
        { enabled: !!session?.user?.id },
    );

    const [countryCode, setCountryCode] = useState<string>(
        user?.countryCode ?? "",
    );
    const [phone, setPhone] = useState<string>(user?.phone ?? "");
    const [showVerificationModal, setShowVerificationModal] =
        useState<boolean>(false);
    const [hideSelf, setHideSelf] = useState<boolean>(false);
    const [checked, setChecked] = useState<boolean>(false);

    const upsertPhoneNumber = api.user.upsertPhoneNumber.useMutation();

    const handleUpsertPhoneNumber = async () => {
        await upsertPhoneNumber.mutateAsync(
            {
                phone: phone,
                countryCode: countryCode,
            },
            {
                onSuccess: async (data) => {
                    if (!data) return;

                    // check if the phone number has changed
                    const phoneChanged =
                        phone !== user?.phone ||
                        countryCode !== user?.countryCode;

                    if (phoneChanged) {
                        // if the phone number has changed, always show verification modal
                        setHideSelf(true);
                        setShowVerificationModal(true);
                    } else if (session?.user.isUserPhoneNumberVerified) {
                        // if the phone number hasn't changed and it's already verified, just close the modal
                        props.setShowModal(false);
                    } else {
                        // if the phone number hasn't changed but it's not verified, show verification modal
                        setHideSelf(true);
                        setShowVerificationModal(true);
                    }

                    await updateSession({
                        ...session,
                        user: {
                            ...session?.user,
                            phone: phone,
                            countryCode: countryCode,
                            isUserPhoneNumberVerified:
                                !phoneChanged &&
                                session?.user.isUserPhoneNumberVerified,
                        },
                    });
                },
            },
        );
    };

    if (!props.showModal) return null;

    return (
        <>
            {!hideSelf && (
                <Modal
                    open={props.showModal}
                    onOpenChange={() => props.setShowModal(false)}
                    title="Add a phone number"
                    description="Get text notifications about your events."
                >
                    {session?.user?.isUserPhoneNumberVerified && (
                        <div className="mt-4 font-semibold text-green-700">
                            Your phone number is already verified. Editing this
                            will change your phone number.
                        </div>
                    )}
                    <div className="flex flex-col justify-start text-start">
                        <div className="mt-4 text-left font-mono text-[12px] font-normal leading-normal">
                            Phone number
                        </div>
                        <div className="flex flex-row flex-nowrap">
                            <div className="flex w-[40%] flex-col justify-center pr-1">
                                <CountryCodeSelector
                                    countryCode={countryCode}
                                    setCountryCode={setCountryCode}
                                />
                            </div>
                            <div className="flex w-full flex-col">
                                <PhoneNumberInput
                                    countryCode={countryCode}
                                    phoneNumber={phone}
                                    setPhoneNumber={setPhone}
                                />
                            </div>
                        </div>
                        <div className="mt-4 text-left text-[12px]">
                            <div className="mb-4 mr-2 flex flex-col items-start">
                                <div className="ml-2">
                                    {` I am 'opting in' to receive SMS
                                    notifications from 'River' also
                                    known as 'River Platform Company'.
                                    Message frequency varies, especially
                                    near event dates. Message and Data
                                    rates may apply. For more
                                    information, please read our `}
                                    <Link
                                        href="https://www.getriver.io/privacy-policy"
                                        className="underline"
                                    >
                                        Privacy Policy
                                    </Link>{" "}
                                    <span> and </span>
                                    <Link
                                        href="https://www.getriver.io/terms-and-conditions"
                                        className="underline"
                                    >
                                        SMS Terms & Conditions
                                    </Link>
                                    <span>. </span>
                                    Text HELP to '18334243892' for help. Text
                                    STOP to cancel.
                                </div>

                                <div className="mt-2 flex flex-row items-center justify-center">
                                    <input
                                        type="checkbox"
                                        className="accent-secondary checked:border-black"
                                        checked={checked}
                                        onChange={(e) => {
                                            setChecked(e.target.checked);
                                        }}
                                    />
                                    <div className="ml-2">I agree</div>
                                </div>
                            </div>
                            Just for event updates. No spam.
                        </div>
                    </div>
                    <div className="mt-4 flex flex-row justify-between">
                        <Button
                            variant="secondary"
                            onClick={() => {
                                props.setShowModal(false);
                            }}
                        >
                            Skip for now
                        </Button>
                        <Button
                            onClick={handleUpsertPhoneNumber}
                            disabled={!checked}
                        >
                            Continue
                        </Button>
                    </div>
                </Modal>
            )}
            {showVerificationModal && (
                <VerifyPhoneNumberModal
                    setShowModal={setShowVerificationModal}
                    verifiedCallback={() => {
                        setHideSelf(false);
                        props.setShowModal(false);
                    }}
                />
            )}
        </>
    );
};

export default PhoneNumberModal;
